import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import HorarioPartido from '../components/HorarioPartido';
import '../styles/jornadas-gestion.css';
import { formatFechaJornada, formatFechaSimple } from '../utils/fechaUtils';

const JornadasGestion = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [jornada, setJornada] = useState(null);
  const [torneos, setTorneos] = useState([]);
  const [torneoSeleccionado, setTorneoSeleccionado] = useState('');
  const [sedesDelTorneo, setSedesDelTorneo] = useState([]);
  const [sedeSeleccionada, setSedeSeleccionada] = useState('');
  const [sedePreferidaId, setSedePreferidaId] = useState(null);
  const [fechaActual, setFechaActual] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');
  const [hayMasJornadas, setHayMasJornadas] = useState({ next: true, prev: true });
  const [loadingFutbolistas, setLoadingFutbolistas] = useState({});
  const [adelantarExpandido, setAdelantarExpandido] = useState(null); // futbolistaId expandido

  // 1. Cargar torneos activos y sede preferida al montar
  useEffect(() => {
    const fetchInicial = async () => {
      try {
        const [torneosRes, sedePreferidaRes] = await Promise.all([
          api.get('/postgres/torneos/activos'),
          api.get('/postgres/jornadas/sede-preferida').catch(() => null)
        ]);

        const torneosData = torneosRes.data.success ? torneosRes.data.data : [];
        setTorneos(torneosData);

        const sedePreferida = sedePreferidaRes?.data?.data?.sede_preferida;
        if (sedePreferida) {
          setSedePreferidaId(sedePreferida.id.toString());
        }

        if (torneosData.length === 0) {
          setLoading(false);
          return;
        }

        // Si hay sede preferida, buscar qué torneo la contiene para auto-seleccionarlo
        if (sedePreferida && torneosData.length > 1) {
          try {
            const torneosDeSede = await api.get(`/postgres/torneos/activos/sede/${sedePreferida.id}`);
            if (torneosDeSede.data.success && torneosDeSede.data.data.length > 0) {
              setTorneoSeleccionado(torneosDeSede.data.data[0].id.toString());
              return;
            }
          } catch (_err) { /* usar primer torneo */ }
        }

        setTorneoSeleccionado(torneosData[0].id.toString());
      } catch (err) {
        console.error('Error al cargar datos iniciales:', err);
        toast.error('Error al cargar los torneos');
        setLoading(false);
      }
    };
    fetchInicial();
  }, []);

  // 2. Al cambiar torneo → cargar sedes y luego jornada
  useEffect(() => {
    if (!torneoSeleccionado) return;
    const fetchSedesYJornada = async () => {
      try {
        const response = await api.get(`/postgres/torneos/${torneoSeleccionado}/sedes-activas`);
        const sedesData = response.data.success ? response.data.data : [];
        setSedesDelTorneo(sedesData);

        // Determinar sede a usar
        let sedeId = '';
        if (sedePreferidaId && sedesData.some(s => s.sede_id.toString() === sedePreferidaId)) {
          sedeId = sedePreferidaId;
        } else if (sedesData.length > 0) {
          sedeId = sedesData[0].sede_id.toString();
        }

        setSedeSeleccionada(sedeId);

        // Cargar jornada directamente con los valores correctos
        if (sedeId) {
          const hoy = new Date().toISOString().split('T')[0];
          setFechaActual(hoy);
          setHayMasJornadas({ next: true, prev: true });
          setJornada(null);
          fetchJornada('next', hoy, sedeId, torneoSeleccionado);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error al cargar sedes del torneo:', err);
        setSedesDelTorneo([]);
        setSedeSeleccionada('');
        setLoading(false);
      }
    };
    fetchSedesYJornada();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torneoSeleccionado, sedePreferidaId]);

  // 3. Al cambiar sede manualmente → cargar jornada
  const handleSedeChange = (nuevaSede) => {
    setSedeSeleccionada(nuevaSede);
    if (nuevaSede && torneoSeleccionado) {
      const hoy = new Date().toISOString().split('T')[0];
      setFechaActual(hoy);
      setHayMasJornadas({ next: true, prev: true });
      setJornada(null);
      fetchJornada('next', hoy, nuevaSede, torneoSeleccionado);
    }
  };

  const fetchJornada = async (direccion = 'next', fechaReferencia = null, sedeOverride = null, torneoOverride = null) => {
    const sedeId = sedeOverride || sedeSeleccionada;
    const torneoId = torneoOverride || torneoSeleccionado;
    setLoading(true);
    try {
      const params = {
        sede_id: sedeId,
        fecha: fechaReferencia || (jornada ? jornada.fecha : fechaActual),
        direccion: direccion === 'current' ? undefined : direccion
      };

      if (torneoId) {
        params.torneo_id = torneoId;
      }

      const response = await api.get('/postgres/jornadas/con-inscripcion', { params });

      if (response.data.success && response.data.data.jornadas.length > 0) {
        const jornadaData = response.data.data.jornadas[0];
        console.log('🔍 DEBUG: Datos de jornada recibidos:', jornadaData);
        console.log('🔍 DEBUG: Fecha de la jornada:', jornadaData.fecha);
        console.log('🔍 DEBUG: Hora de la jornada:', jornadaData.hora);
        console.log('🔍 DEBUG: Número de jornada:', jornadaData.numero);
        console.log('🔍 DEBUG: Futbolistas inscritos detalles:', jornadaData.futbolistas_inscritos_detalles);
        console.log('🔍 DEBUG: Usuario inscrito:', jornadaData.usuario_inscrito);
        console.log('🔍 DEBUG: Puede posponer:', jornadaData.puede_posponer);
        console.log('🔍 DEBUG: Futbolistas pueden adelantarse:', jornadaData.futbolistas_pueden_adelantarse);
        console.log('🔍 DEBUG: Puede adelantar:', jornadaData.puede_adelantar);

        // MOSTRAR TODAS LAS JORNADAS - No saltar jornadas sin inscripciones

        setJornada(jornadaData);
        setFechaActual(jornadaData.fecha);
        setError('');

        // Resetear el estado de navegación cuando encontramos una jornada
        setHayMasJornadas({ next: true, prev: true });
      } else {
        // Si no hay jornadas en la dirección solicitada, actualizar el estado de navegación
        if (direccion === 'next') {
          setError('No hay más jornadas futuras para esta sede');
          setHayMasJornadas(prev => ({ ...prev, next: false }));
        } else if (direccion === 'prev') {
          setError('No hay jornadas anteriores para esta sede');
          setHayMasJornadas(prev => ({ ...prev, prev: false }));
        } else {
          setError('No hay jornadas disponibles para esta sede');
          setHayMasJornadas({ next: false, prev: false });
        }
        // No cambiar la jornada actual si ya hay una
        if (!jornada) {
          setJornada(null);
        }
      }
    } catch (error) {
      console.error('Error al cargar jornada:', error);
      setError('Error al cargar la jornada');
      toast.error('Error al cargar la jornada');
    } finally {
      setLoading(false);
    }
  };

  const navegarJornada = (direccion) => {
    fetchJornada(direccion);
  };

  // Función para ir a la primera jornada sin disputar más cercana a la fecha actual
  const recargarJornadaActual = async () => {
    try {
      console.log('🔍 DEBUG: Buscando primera jornada sin disputar más cercana a hoy...');

      const hoy = new Date().toISOString().split('T')[0];
      const params = {
        sede_id: sedeSeleccionada,
        fecha: hoy,
        direccion: 'next' // Buscar la siguiente jornada desde hoy
      };

      if (torneoSeleccionado) {
        params.torneo_id = torneoSeleccionado;
      }

      console.log('🔍 DEBUG: Parámetros para buscar próxima jornada:', params);
      const response = await api.get('/postgres/jornadas/con-inscripcion', { params });

      console.log('🔍 DEBUG: Respuesta del backend:', response.data);

      if (response.data.success && response.data.data.jornadas.length > 0) {
        const proximaJornada = response.data.data.jornadas[0];
        console.log('✅ DEBUG: Navegando a próxima jornada sin disputar:', proximaJornada.numero, 'Fecha:', proximaJornada.fecha);

        setJornada(proximaJornada);
        setFechaActual(proximaJornada.fecha);
        setError('');
      } else {
        console.log('❌ DEBUG: No se encontraron jornadas futuras');
        toast.error('No hay más jornadas futuras disponibles');
      }
    } catch (error) {
      console.error('Error al buscar próxima jornada:', error);
      toast.error('Error al buscar la próxima jornada');
    }
  };

  const handlePosponerFutbolista = async (futbolista) => {
    console.log('🔍 DEBUG: handlePosponerFutbolista llamado con:', futbolista);
    console.log('🔍 DEBUG: inscripcion_jornada_id:', futbolista.inscripcion_jornada_id);

    if (!window.confirm(`¿Estás seguro de que ${futbolista.nombre} no va a esta jornada?\n\nSe conservará el saldo y se ajustarán automáticamente las inscripciones a las próximas jornadas disponibles.`)) {
      return;
    }

    // CORRECCIÓN PROBLEMA 2: Loading específico por futbolista
    setLoadingFutbolistas(prev => ({ ...prev, [futbolista.id]: true }));
    try {
      console.log('🔍 DEBUG: Enviando petición de posposición...');
      const response = await api.post('/postgres/jornadas/posponer-futbolista', {
        inscripcion_jornada_id: futbolista.inscripcion_jornada_id
      });

      console.log('🔍 DEBUG: Respuesta recibida:', response.data);
      if (response.data.success) {
        setError('');
        // Mostrar mensaje de éxito
        toast.success(`✅ ${response.data.message}`);
        console.log('✅ DEBUG: Acción completada - navegando a próxima jornada sin disputar');
        // Navegar a la primera jornada sin disputar más cercana a hoy
        await recargarJornadaActual();
      } else {
        console.log('🔍 DEBUG: Error en respuesta:', response.data.message);
        setError(response.data.message || 'Error al posponer el futbolista');
        toast.error(response.data.message || 'Error al posponer el futbolista');
      }
    } catch (error) {
      console.error('🔍 DEBUG: Error al posponer futbolista:', error);
      console.error('🔍 DEBUG: Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Error al posponer el futbolista. Inténtalo de nuevo.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      // CORRECCIÓN PROBLEMA 2: Limpiar loading específico
      setLoadingFutbolistas(prev => ({ ...prev, [futbolista.id]: false }));
    }
  };

  // Toggle del picker inline: si ya está expandido, lo cierra; si no, lo abre
  const handleToggleAdelantar = (futbolistaId) => {
    setAdelantarExpandido(prev => prev === futbolistaId ? null : futbolistaId);
  };

  // Ejecutar adelantar con la jornada elegida por el usuario
  const handleAdelantarFutbolista = async (futbolista, jornadaId, jornadaCancelarId) => {
    setLoadingFutbolistas(prev => ({ ...prev, [futbolista.id]: true }));
    try {
      const response = await api.post('/postgres/jornadas/adelantar-futbolista', {
        futbolista_id: futbolista.id,
        jornada_id: jornadaId,
        jornada_cancelar_id: jornadaCancelarId
      });

      if (response.data.success) {
        setError('');
        setAdelantarExpandido(null);
        toast.success(`✅ ${response.data.message}`);
        await recargarJornadaActual();
      } else {
        setError(response.data.message || 'Error al adelantar el futbolista');
        toast.error(response.data.message || 'Error al adelantar el futbolista');
      }
    } catch (error) {
      console.error('Error al adelantar futbolista:', error);
      const errorMessage = error.response?.data?.message || 'Error al adelantar el futbolista. Inténtalo de nuevo.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingFutbolistas(prev => ({ ...prev, [futbolista.id]: false }));
    }
  };

  const handleInscribirse = () => {
    // Redirigir a la página de compra de paquetes
    navigate('/products');
  };

  const formatearFecha = (fecha) => formatFechaJornada(fecha);
  // Para fecha_limite_inscripcion: mostrar como fecha única, sin rango sáb-dom
  const formatearFechaLimite = (fecha) => formatFechaSimple(fecha, true);

  const esFechaLimiteVencida = (fechaLimite) => {
    return new Date(fechaLimite) < new Date();
  };

  if (loading) {
    return (
      <div className="jornadas-gestion">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando jornada...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jornadas-gestion">
      <div className="jornadas-header">
        <h1>Gestión de Jornadas</h1>

        <div className="jornadas-selectores">
          {/* Selector de Torneo (master) - solo si hay más de 1 torneo activo */}
          {torneos.length > 1 && (
            <div className="sede-selector">
              <label htmlFor="torneo">Torneo:</label>
              <select
                id="torneo"
                value={torneoSeleccionado}
                onChange={(e) => setTorneoSeleccionado(e.target.value)}
                className="sede-select"
              >
                {torneos.map(torneo => (
                  <option key={torneo.id} value={torneo.id}>
                    {torneo.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Selector de Sede (dependiente del torneo) */}
          {sedesDelTorneo.length > 0 && (
            <div className="sede-selector">
              <label htmlFor="sede">Sede:</label>
              <select
                id="sede"
                value={sedeSeleccionada}
                onChange={(e) => handleSedeChange(e.target.value)}
                className="sede-select"
              >
                {sedesDelTorneo.map(sede => (
                  <option key={sede.sede_id} value={sede.sede_id}>
                    {sede.sede_nombre} - {sede.sede_poblacion}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {jornada && (
        <div className="jornada-container">
          {/* Navegación de Jornadas */}
          <div className="jornada-navigation">
            <button
              className={`nav-button prev ${!hayMasJornadas.prev ? 'disabled' : ''}`}
              onClick={() => navegarJornada('prev')}
              disabled={!hayMasJornadas.prev}
            >
              ← Anterior
            </button>

            <div className="jornada-info-header">
              <h2>Jornada {jornada.numero}</h2>
              <p className="torneo-nombre">{jornada.torneo_nombre}</p>
            </div>

            <button
              className={`nav-button next ${!hayMasJornadas.next ? 'disabled' : ''}`}
              onClick={() => navegarJornada('next')}
              disabled={!hayMasJornadas.next}
            >
              Siguiente →
            </button>
          </div>

          {/* Información Principal de la Jornada */}
          <div className="jornada-card">
            <div className="jornada-details">
              <div className="detail-item">
                <span className="label">Fecha:</span>
                <span className="value">{formatearFecha(jornada.fecha)}</span>
              </div>

              <div className="detail-item">
                <span className="label">Hora:</span>
                <div className="value">
                  <div className="time-notice">Recibirás horario definitivo {jornada.torneo_nombre?.toLowerCase().includes('summer') && jornada.sede_nombre?.toLowerCase().includes('alicante') ? 'jueves' : 'viernes'} por la tarde</div>
                </div>
              </div>

              <div className="detail-item">
                <span className="label">Sede:</span>
                <span className="value">{jornada.sede_nombre}</span>
              </div>

              <div className="detail-item">
                <span className="label">Ubicación:</span>
                <span className="value">{jornada.sede_direccion}, {jornada.sede_poblacion}</span>
              </div>

              <div className="detail-item">
                <span className="label">Fecha límite inscripción:</span>
                <span className={`value ${esFechaLimiteVencida(jornada.fecha_limite_inscripcion) ? 'expired' : ''}`}>
                  {formatearFechaLimite(jornada.fecha_limite_inscripcion)}
                </span>
              </div>
            </div>

            {/* Estado de Inscripción y Acciones */}
            <div className="inscripcion-status">
              {/* SECCIÓN 1: Futbolistas inscritos en esta jornada */}
              {jornada.usuario_inscrito && (
                <div className="inscrito">
                  <div className="status-badge inscrito-badge">
                    ✅ Estás inscrito
                  </div>
                  <p>Tienes {jornada.futbolistas_inscritos_detalles?.length || 0} futbolista(s) inscrito(s) en esta jornada:</p>

                  {/* Lista de futbolistas inscritos */}
                  <div className="futbolistas-inscritos-lista">
                    {jornada.futbolistas_inscritos_detalles?.map((futbolista) => (
                      <div key={futbolista.id} className="futbolista-inscrito-item">
                        <div className="futbolista-info">
                          <span className="futbolista-nombre">⚽ {futbolista.nombre}</span>
                          <HorarioPartido
                            futbolistaId={futbolista.id}
                            jornadaId={jornada.id}
                            className="futbolista-horario"
                          />
                          <span className="futbolista-estado">✅ Inscrito</span>
                        </div>

                        <div className="futbolista-acciones">
                          {/* CORRECCIÓN: Usar lógica específica por futbolista */}
                          {futbolista.puede_posponer && (
                            <button
                              className="btn-posponer"
                              onClick={() => handlePosponerFutbolista(futbolista)}
                              disabled={loadingFutbolistas[futbolista.id] || false}
                            >
                              {loadingFutbolistas[futbolista.id] ? '⏳ Procesando...' : '❌ No voy'}
                            </button>
                          )}
                          {jornada.es_proxima_jornada && !futbolista.puede_posponer && (
                            <span className="plazo-vencido">⏰ Plazo vencido</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              )}

              {/* SECCIÓN 2: Futbolistas que pueden adelantarse - SOLO EN PRÓXIMA JORNADA */}
              {jornada.es_proxima_jornada && jornada.futbolistas_pueden_adelantarse && jornada.futbolistas_pueden_adelantarse.length > 0 && (
                <div className="puede-adelantar">
                  <div className="status-badge saldo-disponible-badge">
                    ⚡ Puedes adelantar futbolistas
                  </div>
                  <p>Estos futbolistas están inscritos en jornadas posteriores y pueden adelantarse:</p>

                  <div className="futbolistas-saldo-lista">
                    {jornada.futbolistas_pueden_adelantarse.map((futbolista) => (
                      <div key={futbolista.id} className="futbolista-saldo-item">
                        <div className="futbolista-info">
                          <span className="futbolista-nombre">⚽ {futbolista.nombre}</span>
                          <HorarioPartido
                            futbolistaId={futbolista.id}
                            jornadaId={jornada.id}
                            className="futbolista-horario"
                          />
                          <span className="futbolista-saldo">💰 {futbolista.saldo_jornadas} jornadas en esta sede</span>
                        </div>
                        <button
                          className="btn-adelantar"
                          onClick={() => handleToggleAdelantar(futbolista.id)}
                          disabled={loadingFutbolistas[futbolista.id] || !futbolista.puede_adelantar}
                        >
                          {loadingFutbolistas[futbolista.id] ? '⏳ Procesando...' :
                           !futbolista.puede_adelantar ? '⏰ No disponible' :
                           adelantarExpandido === futbolista.id ? '✖ Cancelar' : '✅ Sí voy'}
                        </button>

                        {/* Picker inline: elegir qué jornada soltar */}
                        {adelantarExpandido === futbolista.id && futbolista.jornadas_futuras && futbolista.jornadas_futuras.length > 0 && (
                          <div className="jornadas-futuras-picker">
                            <p className="picker-titulo">¿Qué jornada te quieres adelantar?</p>
                            <div className="picker-opciones">
                              {futbolista.jornadas_futuras.map((jf) => (
                                <button
                                  key={jf.id}
                                  className="btn-jornada-futura"
                                  onClick={() => handleAdelantarFutbolista(futbolista, jornada.id, jf.id)}
                                  disabled={loadingFutbolistas[futbolista.id]}
                                >
                                  <span className="jf-numero">J{jf.numero}</span>
                                  <span className="jf-fecha">{new Date(jf.fecha).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                                  {jf.sede_nombre && <span className="jf-sede">📍 {jf.sede_nombre}</span>}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECCIÓN INFO: Información de posposición - SOLO si hay futbolistas inscritos */}
              {jornada.usuario_inscrito && (
                <div className="info-posposicion">
                  <div className="info-header">
                    <span className="info-icon">⏭️</span>
                    <strong className="info-title">¿Cómo funciona la posposición?</strong>
                  </div>
                  <ul className="info-list">
                    <li>Solo puedes posponer futbolistas en la <strong>próxima jornada a disputar</strong></li>
                    <li><strong>Plazo:</strong> hasta el {formatearFechaLimite(jornada.fecha_limite_inscripcion)}</li>
                    <li>El <strong>saldo se conserva</strong> y se reajustan automáticamente las inscripciones</li>
                  </ul>
                </div>
              )}

              {/* SECCIÓN 3: No inscrito — se muestra siempre (también cuando hay adelantables) */}
              {!jornada.usuario_inscrito && (
                <div className="no-opciones">
                  {jornada.puede_inscribirse ? (
                    <>
                      <div className="status-badge no-inscrito-badge">
                        ❌ No estás inscrito
                      </div>
                      <p>Puedes inscribirte comprando un paquete de jornadas.</p>
                      <button
                        className="btn-inscribirse"
                        onClick={handleInscribirse}
                      >
                        💰 Comprar paquete
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="status-badge fecha-vencida-badge">
                        ⏰ Fecha límite vencida
                      </div>
                      <p>Ya no es posible inscribirse en esta jornada.</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JornadasGestion;
